import { useId, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";
import { useDismissable } from "./useDismissable";

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Which inline edge the panel docks to (logical — flips under RTL). Default "end". */
  side?: "start" | "end";
}

/**
 * Drawer — side panel sibling of Modal (same focus-trap/Esc/scroll-lock behavior). Docks to the
 * logical inline edge so it mirrors correctly under dir="rtl".
 */
export function Drawer({ open, onClose, title, children, footer, side = "end" }: DrawerProps) {
  const titleId = useId();
  const panelRef = useDismissable(open, onClose);
  if (!open) return null;

  return createPortal(
    <div
      className={cx("nv-drawer-overlay", side === "end" && "nv-drawer-overlay--end")}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className="nv-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className="nv-drawer__header">
          <h2 className="nv-drawer__title" id={titleId}>
            {title}
          </h2>
          <button type="button" className="nv-iconbtn" aria-label="Fermer" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="nv-drawer__body">{children}</div>
        {footer && <footer className="nv-drawer__footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
